export type FetcherCatalogueLevel =
  | "config"
  | "report"
  | "interface"
  | "lookup"
  | "brand";

export interface FetcherCatalogueRow {
  level: FetcherCatalogueLevel | string;
  item_id: number | null;
  item_name: string | null;
  shared_uid: string | null;
  interface_id: number | null;
  interface_name: string | null;
  report_id: number | null;
  report_name: string | null;
  recipe: string | null;
  duplicate_of: string | null;
  used_by_brands: number | null;
  child_configs: number | null;
  child_reports: number | null;
  own_objects: number | null;
  url_path: string | null;
  uri_value: string | null;
  common_dynamic_var: string | null;
  collection_type: string | null;
  collection_type_id: number | null;
  collection_method: string | null;
  collection_method_id: number | null;
  sub_filtering_method: string | null;
  sub_filtering_method_id: number | null;
  format_type: string | null;
  format_type_id: number | null;
  account_type: string | null;
  is_active: number | null;
  lookup_group: number | null;
  lookup_usage: number | null;
  modified: string | null;
  modified_by: string | null;
}

/** The rows of one level, out of whatever the query returned. */
export function selectCatalogueRows(
  mainTablesData: any,
  level: FetcherCatalogueLevel,
): FetcherCatalogueRow[] {
  const queryOutput = mainTablesData?.query_output;
  if (!Array.isArray(queryOutput)) return [];
  return queryOutput.filter((row: FetcherCatalogueRow) => row?.level === level);
}

/** How many rows of one level came back - what the tab badges show. */
export function countCatalogueRows(
  mainTablesData: any,
  level: FetcherCatalogueLevel,
): number {
  return selectCatalogueRows(mainTablesData, level).length;
}

/** Ids as strings, for React keys and comparison with URL params. */
export const rowId = (value: number | string | null): string =>
  value === null || value === undefined ? "" : String(value);

/**
 * `duplicate_of` holds the twin's id, and may hold several separated by commas.
 * Null, empty and whitespace-only all mean "not a duplicate".
 */
export function parseDuplicateOf(value: string | null): string[] {
  if (!value) return [];
  return String(value)
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

export type FetcherDetailSection = "item" | "object" | "brand" | "child";
export type FetcherDetailScope = "own" | "inherited";

export interface FetcherDetailRow {
  section: FetcherDetailSection | string;
  scope: FetcherDetailScope | string | null;
  level: string | null;
  item_id: string | null;
  item_name: string | null;
  shared_uid: string | null;
  item_detail_1: string | null;
  item_detail_2: string | null;
  item_detail_3: string | null;
  object_id: string | null;
  object_type: string | null;
  object_value: string | null;
  field_type: string | null;
  field_value: string | null;
  is_union_key: number | string | null;
  is_effective: number | string | null;
  drop_reason: string | null;
  is_deprecated: number | string | null;
  child_id: string | null;
  child_name: string | null;
  child_detail: string | null;
  brand_id: string | null;
  brand_name: string | null;
  brand_active: number | string | null;
  want_run: number | string | null;
  raw_table: string | null;
  modified: string | null;
  modified_by: string | null;
}

/** Every row of one section out of a detail response. */
export function selectDetailRows(
  detailsData: any,
  section: FetcherDetailSection,
): FetcherDetailRow[] {
  const queryOutput = detailsData?.query_output;
  if (!Array.isArray(queryOutput)) return [];
  return queryOutput.filter(
    (row: FetcherDetailRow) => row?.section === section,
  );
}

/**
 * The `object` rows of one scope. `inherited` is what the level above already
 * sets; `own` is what this item sets itself.
 */
export function selectDetailObjects(
  detailsData: any,
  scope: FetcherDetailScope,
): FetcherDetailRow[] {
  return selectDetailRows(detailsData, "object").filter(
    (row) => row.scope === scope,
  );
}

export function selectDetailBrands(detailsData: any): FetcherDetailRow[] {
  return selectDetailRows(detailsData, "brand").filter(
    (row) => row.brand_id !== null && row.brand_id !== undefined,
  );
}

export function selectDevObjectRows(devObjsData: any): FetcherDetailRow[] {
  return selectDetailRows(devObjsData, "object");
}

/**
 * The brand-trace query returns one flat list for a single brand: its own
 * `section=brand` row, then a `section=connection` row per config it collects
 * with, then the `section=object` rows behind each of those.
 */
export interface FetcherBrandTraceRow {
  section: string;
  brand_id: number | null;
  brand_name: string | null;
  account_active: number | null;
  account_type: string | null;
  keeper_id: string | null;
  username: string | null;
  login_url: string | null;
  dynamic_var: string | null;
  run_from_date: string | null;
  site_name: string | null;
  connection_count: number | null;
  interface_id: number | null;
  interface_name: string | null;
  config_id: number | null;
  config_name: string | null;
  report_id: number | null;
  report_name: string | null;
  collection_type: string | null;
  recipe: string | null;
  link_id: number | null;
  raw_table: string | null;
  want_run: number | null;
  status: string | null;
  connection_state: string | null;
  object_id: number | null;
  object_level: string | null;
  level_rank: number | null;
  field_type: string | null;
  field_value: string | null;
  /** the value as the query wants it shown, when it differs from field_value */
  field_value_display: string | null;
  is_effective: number | null;
  is_union_key: number | null;
  is_deprecated: number | null;
  drop_reason: string | null;
}

/** Every row of one section out of a brand-trace response. */
export function selectBrandTraceRows(
  brandTraceData: any,
  section: string,
): FetcherBrandTraceRow[] {
  const queryOutput = brandTraceData?.query_output;
  if (!Array.isArray(queryOutput)) return [];
  return queryOutput.filter(
    (row: FetcherBrandTraceRow) => row?.section === section,
  );
}

/**
 * The traced brand itself. The query is run for one brand, so there is only
 * ever one `section=brand` row.
 */
export function selectBrandTraceBrand(
  brandTraceData: any,
): FetcherBrandTraceRow | undefined {
  return selectBrandTraceRows(brandTraceData, "brand")[0];
}
